/**
 * UI strings. English defines the key set; every other locale must provide
 * exactly the same keys (enforced by the `Messages` type).
 *
 * Placeholders use `{name}` and are filled by `translate`.
 */

export const LOCALES = ['fr', 'en'] as const
export type Locale = (typeof LOCALES)[number]

/** Pilot default (Douala/Yaoundé). Browser preferences win when supported. */
/** Used when none of the browser's preferred languages is supported. */
export const DEFAULT_LOCALE: Locale = 'en'

const en = {
  'try.title': 'See it on you before you order.',
  'try.intro': 'Open a product link from your seller to try the item on yourself.',
  'try.link': 'Product link',
  'try.hint': 'Ask your seller for their TryOnMe product link, or copy it from their post.',
  'try.invalid': 'Paste a product link from this TryOnMe site. It should contain /p/ followed by the product code.',
  'try.open': 'Open product',
  'try.private': 'No account needed. Your photo stays private.',
  'try.next': 'Three simple steps',
  'try.step1': 'Open the item your seller shared.',
  'try.step2': 'Add your photo and see the item on you.',
  'try.step3': 'Choose your size and message the seller on WhatsApp.',
  'a11y.skip': 'Skip to content',
  'a11y.navigation': 'Main navigation',
  'a11y.loading': 'Loading page…',
  'catalog.search': 'Search products',
  'catalog.clear': 'Clear search',
  'catalog.count': '{count} products',
  'catalog.noResults': 'No matching products. Try another name.',
  'seo.studio': 'Seller Studio',
  'seo.look': 'Private shared look',
  'seo.poster': 'Print your shop poster',
  'home.shopperLabel': 'For shoppers',
  'home.sellerLabel': 'For sellers',
  'home.shopperBrief': 'See products on yourself.',
  'home.sellerBrief': 'Turn one product photo into try-on experiences and social content.',
  'app.name': 'TryOnMe',
  'language.label': 'Language',
  'language.switchTo': 'Français',
  'home.shoppers': 'For shoppers: See products on yourself.',
  'home.sellerIntro': 'For sellers: Turn one product photo into try-on experiences and social content.',
  'home.title': 'Try it before you buy it.',
  'home.subtitle': 'Sell it before you photograph a model.',
  'home.createShop': 'Create seller content',
  'home.seeExample': 'Try something on',
  'product.loading': 'Loading product…',
  'product.notFound': 'This product is no longer available.',
  'product.loadError': "We couldn't load this product. Check your connection and try again.",
  'product.retry': 'Try again',
  'product.soldBy': 'Sold by {shop}',
  'tryOn.cta': 'Try it on me',
  'whatsapp.order': 'Order on WhatsApp',
  'whatsapp.message': 'Hi {shop} 👋 I\'d like "{title}" ({price}). Seen here: {url}',
  'whatsapp.messageNoPrice': 'Hi {shop} 👋 I\'d like "{title}". Seen here: {url}',
  'whatsapp.messageWithLook':
    'Hi {shop} 👋 I\'d like "{title}" ({price}). Here\'s how it looks on me: {look}',
  'whatsapp.messageWithLookNoPrice': 'Hi {shop} 👋 I\'d like "{title}". Here\'s how it looks on me: {look}',
  'look.toggle': 'Send my look to the seller',
  'look.consent':
    'Only {shop} can see this image, for 30 days. Your original photo is never shared.',
  'look.sending': 'Preparing your look…',
  'look.ready': 'Your look will be included in your WhatsApp message.',
  'look.error': "We couldn't share your look. You can still order on WhatsApp.",
  'looks.title': 'Looks from your buyers',
  'looks.empty': 'When buyers send you their look, it appears here.',
  'looks.sharedOn': 'Sent on {date}',
  'looks.expired': 'This look is no longer available (removed by the buyer or expired).',
  'looks.signIn': 'Sign in to see this look.',
  'stats.views': '{count} views',
  'stats.tryOns': '{count} try-ons',
  'stats.whatsapp': '{count} WhatsApp',
  'stats.looks': '{count} looks',
  'disclosure.ai': 'AI-generated preview. Not a sizing guarantee.',
  'selfie.title': 'Your photo',
  'selfie.guidance':
    'Stand facing the camera, alone, in good light. Fill the frame from head to hips, with little empty space around you.',
  'selfie.camera': 'Take a photo',
  'selfie.gallery': 'Choose from gallery',
  'selfie.privacy': 'Your photo stays private.',
  'selfie.back': 'Back',
  'progress.preparing': 'Preparing your photo',
  'progress.creating': 'Creating your try-on',
  'progress.finishing': 'Almost done',
  'result.title': "Here's how it looks on you",
  'result.alt': 'Try-on preview of {title}',
  'result.another': 'Try another photo',
  'error.person_not_detected':
    "We couldn't see you clearly. Take a front-facing photo with your face and shoulders visible.",
  'error.multiple_people': 'Only one person should be in the photo.',
  'error.image_too_small': 'This photo is too small. Use a larger, sharper photo.',
  'error.unsupported_image': 'Use a JPG or PNG photo.',
  'error.image_too_large': 'This photo is too large (10 MB max).',
  'error.garment_unsuitable': "This item can't be tried on yet. Message the seller on WhatsApp.",
  'error.content_rejected': "This photo can't be used. Try a different one.",
  'error.temporary': 'Something went wrong on our side. Try again in a moment.',
  'error.demo_limit_reached':
    'The demo has reached its try-on limit for now. You can still message the seller on WhatsApp!',
  'error.network': 'No connection. Check your internet and try again.',
  'studio.title': 'Studio',
  'studio.loading': 'Loading your shop…',
  'studio.signIn.title': 'Sell more with try-on',
  'studio.signIn.body':
    'Sign in to create your shop and share products your buyers can try on before ordering.',
  'studio.signIn.google': 'Continue with Google',
  'studio.signIn.failed': "Sign-in didn't work. Please try again.",
  'studio.signIn.emailNotVerified': 'Your Google email address must be verified.',
  'studio.logout': 'Sign out',
  'shop.title': 'Your shop',
  'shop.name': 'Shop name',
  'shop.country': 'Country',
  'shop.whatsapp': 'WhatsApp number',
  'shop.whatsappHint': 'Buyers will message you on this number.',
  'shop.language': 'Language of your messages',
  'shop.save': 'Create my shop',
  'shop.saving': 'Saving…',
  'product.edit': 'Edit product',
  'product.editShort': 'Edit',
  'product.replacePhoto': 'Replace the photo (optional)',
  'product.replacePhotoHint': 'Leave empty to keep the current photo. Your share link stays the same.',
  'product.delete': 'Delete product',
  'product.deleteConfirm': 'Delete “{title}”? Its link will stop working, and its photo, worn photos and Status images will be erased.',
  'models.delete': 'Delete',
  'models.deleteConfirm': 'Delete {name}? Their consent is withdrawn, and their photo and every image made with them are erased.',
  'studioError.generation_in_progress': 'A creation with this item is still running. Try again in a minute.',
  'admin.eyebrow': 'Operator',
  'admin.title': 'Admin dashboard',
  'admin.updated': 'Updated at {time}',
  'admin.refresh': 'Refresh',
  'admin.last7': '{count} in the last 7 days',
  'admin.kpi.sellers': 'Sellers',
  'admin.kpi.shops': 'Shops',
  'admin.kpi.products': 'Products',
  'admin.kpi.models': 'Models',
  'admin.kpi.tryOns': 'Buyer try-ons',
  'admin.kpi.stills': 'Worn photos',
  'admin.kpi.statusImages': 'Status images',
  'admin.kpi.videos': 'Status videos',
  'admin.kpi.looks': 'Active looks',
  'admin.kpi.views': 'Views (7 d)',
  'admin.kpi.whatsapp': 'WhatsApp taps (7 d)',
  'admin.units.title': 'YouCam units',
  'admin.units.committed': 'Committed',
  'admin.units.left': 'left before the ceiling',
  'admin.units.reserve': '{count} units at the end are reserved for sellers and the demo (buyers stop before).',
  'admin.units.used24h': 'used in 24 h',
  'admin.units.used7d': 'used in 7 days',
  'admin.daily.title': 'Last 14 days',
  'admin.op.clothing_try_on': 'Buyer try-on',
  'admin.op.seller_try_on_still': 'Worn photo',
  'admin.op.seller_promo_video': 'Status video',
  'admin.jobs.title': 'Job outcomes (7 days)',
  'admin.failures.title': 'Failure reasons (7 days)',
  'admin.failures.none': 'No failures. 🎉',
  'admin.empty': 'Nothing yet.',
  'admin.recent.title': 'Recent jobs',
  'admin.shops.title': 'Shops',
  'admin.buyer': 'Buyer',
  'admin.col.when': 'When',
  'admin.col.operation': 'Operation',
  'admin.col.status': 'Status',
  'admin.col.shop': 'Shop',
  'admin.col.duration': 'Duration',
  'admin.col.units': 'Units',
  'admin.col.error': 'Error',
  'admin.col.since': 'Since',
  'admin.link': 'Admin',
  'admin.nav.overview': 'Overview',
  'admin.nav.jobs': 'Jobs',
  'admin.nav.shops': 'Shops',
  'admin.nav.studio': 'Back to Studio',
  'admin.nav.collapse': 'Collapse menu',
  'admin.nav.expand': 'Expand menu',
  'admin.nav.open': 'Open menu',
  'admin.nav.close': 'Close menu',
  'error.photo_expired': 'Your earlier photo has been erased (we keep photos 24 hours at most). Please take a new one.',
  'selfie.reuse': 'Use my earlier photo',
  'selfie.reuseHint': 'The photo you used for another item. It stays private.',
  'selfie.or': 'or a new photo',
  'shop.page.tagline': 'Try every item on yourself before you order.',
  'shop.page.empty': 'No items available right now. Come back soon!',
  'shop.page.notFound': 'This shop does not exist.',
  'shop.page.tryOn': 'Try it on',
  'shop.page.contact': 'Message on WhatsApp',
  'shop.page.message': 'Hello {shop} 👋 I saw your shop on TryOnMe: {url}',
  'shop.page.seeAll': 'See the whole shop',
  'shop.page.tryAnother': 'Try another item',
  'shop.link.title': 'Your online shop',
  'shop.link.hint': 'One link for your whole catalogue: put it in your WhatsApp Status, TikTok or Instagram bio.',
  'checklist.title': 'Get started',
  'checklist.progress': '{done} of {total} steps done',
  'checklist.model': 'Add yourself as a model',
  'checklist.product': 'Add your first product',
  'checklist.still': 'Create a worn photo',
  'checklist.share': 'Share your shop link',
  'checklist.tryOn': 'Your first buyer tries an item',
  'checklist.tryOnHint': 'Happens as soon as a buyer tries an item from your link.',
  'checklist.copyLink': 'Copy my link',
  'checklist.go': 'Start',
  'checklist.hide': 'Hide the checklist',
  'checklist.done': '(done)',
  'checklist.todo': '(to do)',
  'poster.kicker': 'Try it on before you buy',
  'poster.shopTagline': 'Try every item on yourself, then order on WhatsApp.',
  'poster.scanShop': 'Scan to see the shop and try items on yourself',
  'poster.scanProduct': 'Scan to try it on yourself',
  'poster.qrAlt': 'QR code linking to {url}',
  'poster.footer': 'Your photo stays private · TryOnMe',
  'poster.print': 'Print',
  'poster.open': 'QR poster',
  'legal.privacy': 'Privacy',
  'legal.terms': 'Terms',
  'legal.updated': 'Last updated: {date}',
  'legal.contact': 'Contact:',
  'legal.signInPrefix': 'By continuing, you accept the',
  'legal.and': 'and the',
  'deleteShop.title': 'Delete my shop and account',
  'deleteShop.body': 'Your products, models, worn photos and Status media are erased, your links stop working and you are signed out. This cannot be undone.',
  'deleteShop.start': 'Delete my shop…',
  'deleteShop.confirmLabel': 'Type “{name}” to confirm',
  'deleteShop.mismatch': 'The name does not match your shop name.',
  'deleteShop.confirm': 'Delete permanently',
  'deleteShop.deleting': 'Deleting…',
  'looks.new': 'New',
  'looks.newBannerOne': 'A buyer sent you a new look',
  'looks.newBannerMany': 'You have {count} new looks from buyers',
  'looks.newCta': 'See',
  'credits.label': 'credits',
  'credits.history': 'History',
  'credits.hideHistory': 'Hide history',
  'credits.noHistory': 'No credit movements yet.',
  'credits.prices': 'Buyer try-on: {tryOn} · worn photo: {still} · clean background: {cleanup} · video: {video} · Status image: free',
  'credits.low': 'Your credits are running low.',
  'credits.empty': 'You have no credits left: buyers cannot try your items on.',
  'credits.topUp': 'Ask for a top-up',
  'credits.topUpSubject': 'TryOnMe credit top-up',
  'credits.costOne': '1 credit',
  'credits.costMany': '{count} credits',
  'credits.balanceShort': 'balance: {count}',
  'credits.notEnough': 'not enough credits',
  'credits.kind.welcome': 'Welcome credits',
  'credits.kind.grant': 'Top-up',
  'credits.kind.try_on': 'Buyer try-on',
  'credits.kind.still': 'Worn photo',
  'credits.kind.video': 'Status video',
  'credits.kind.refund': 'Refund (failed creation)',
  'studioError.insufficient_credits': 'Not enough credits for this. Ask for a top-up.',
  'error.try_on_unavailable': 'Try-on is not available for this item right now. You can still order on WhatsApp.',
  'admin.col.credits': 'Credits',
  'admin.grant.add': 'Add',
  'admin.grant.amount': 'Credits to add',
  'admin.grant.note': 'Reason',
  'admin.grant.done': 'New balance: {count}',
  'admin.nav.credits': 'Credits',
  'admin.credits.add': 'Add credits',
  'admin.credits.balance': 'balance {count}',
  'admin.credits.negativeHint': 'Use a negative amount to correct a mistake (never below 0).',
  'admin.credits.notePlaceholder': 'e.g. pilot top-up',
  'admin.credits.review': 'Review',
  'admin.credits.summaryAdd': 'Add {count} credits to {shop}',
  'admin.credits.summaryRemove': 'Remove {count} credits from {shop}',
  'admin.credits.wouldBeNegative': 'The balance cannot go below 0.',
  'admin.credits.confirm': 'Confirm',
  'admin.credits.working': 'Saving…',
  'admin.credits.edit': 'Change',
  'admin.credits.done': '{shop}: new balance {count}.',
  'admin.credits.recent': 'Recent top-ups',
  'tryOnSwitch.label': 'Try-on for my buyers',
  'tryOnSwitch.onHint': 'Buyers can try your items on (it uses your credits).',
  'tryOnSwitch.offHint': 'Off: buyers see your items and order on WhatsApp, without try-on. No credits are used.',
  'shop.page.taglineNoTryOn': 'Discover the items and order on WhatsApp.',
  'poster.scanShopNoTryOn': 'Scan to see the shop',
  'poster.scanProductNoTryOn': 'Scan to see the item',
  'nav.videos': 'Videos',
  'videos.title': 'Videos',
  'videos.hint': 'For TikTok, Reels, WhatsApp Status and Facebook. Pick a photo, a style and a format.',
  'videos.step.photo': '1. Choose a worn photo',
  'videos.step.style': '2. Choose a style',
  'videos.step.format': '3. Choose a format',
  'videos.style.runway': 'Runway',
  'videos.style.runway.hint': 'Natural movement and pose',
  'videos.style.zoom': 'Zoom',
  'videos.style.zoom.hint': 'Slow zoom on the outfit',
  'videos.style.turn': 'Turn',
  'videos.style.turn.hint': 'The model turns to show it',
  'videos.format.vertical': 'Vertical 9:16',
  'videos.format.vertical.hint': 'TikTok, Reels, WhatsApp Status, Shorts',
  'videos.format.square': 'Square 1:1',
  'videos.format.square.hint': 'Facebook and Instagram feed',
  'videos.create': 'Create the video',
  'videos.working': 'Your video is being made: 1 to 2 minutes. Stay on this page.',
  'videos.failed': 'The video could not be made. Your credits were refunded: try another photo or style.',
  'videos.ready': 'New',
  'videos.mine': 'My videos',
  'videos.pending': '{count} video(s) still being made. Come back in a minute.',
  'videos.noPhotos.title': 'Start with a worn photo',
  'videos.noPhotos.text': 'A video starts from a worn photo. In Catalogue, open a product and tap "Create worn photo".',
  'videos.noPhotos.action': 'Go to Catalogue',
  'credits.kind.cleanup': 'Clean background',
  'clean.open': 'Clean background',
  'clean.title': 'Clean background',
  'clean.hint': 'Removes the bed, wall or floor behind your item and puts it on a plain background. Your current photo stays until you choose.',
  'clean.backdrop': 'Background',
  'clean.backdrop.white': 'White',
  'clean.backdrop.grey': 'Light grey',
  'clean.backdrop.cream': 'Cream',
  'clean.create': 'Clean the photo',
  'clean.working': 'Cleaning…',
  'clean.wait': 'About 30 seconds.',
  'clean.before': 'Before',
  'clean.after': 'After',
  'clean.beforeAlt': 'Current photo of {title}',
  'clean.afterAlt': 'Clean photo of {title}',
  'clean.use': 'Use this photo',
  'clean.again': 'Try another background',
  'clean.useHint': 'Your link, counters and worn photos stay the same.',
  'clean.failed': 'We could not separate the item from the background. Your credits were refunded: try a photo where the item is fully visible.',
  'clean.close': 'Close',
  'product.size': 'Size',
  'product.sizeHint': 'Choose your size: it will be in your WhatsApp message.',
  'product.soldOut': 'Sold out',
  'whatsapp.askAvailability': 'Ask if it is back',
  'whatsapp.stillAvailable': 'Is it available again?',
  'whatsapp.size': 'Size: {size}',
  'whatsapp.reference': 'Ref: {reference}',
  'stock.title': 'Sizes and stock',
  'stock.inStock': 'In stock',
  'stock.outOfStock': 'Out of stock: buyers can only ask when it is back',
  'stock.tapHint': 'Tap a size to mark it sold out (or back in stock).',
  'stock.markSoldOut': '(available, tap to mark sold out)',
  'stock.markAvailable': '(sold out, tap to mark available)',
  'stock.remove': 'Remove size {size}',
  'stock.addSizes': 'Add sizes',
  'stock.preset.one': 'One size',
  'stock.custom': 'Other sizes',
  'stock.customPlaceholder': 'e.g. 2 ans, 4 ans',
  'stock.add': 'Add',
  'studioError.invalid_size': 'A size must be 1 to 16 characters.',
  'studioError.duplicate_size': 'This size is already listed.',
  'studioError.too_many_sizes': 'At most 20 sizes per item.',
  'nav.orders': 'Orders',
  'orders.title': 'Orders',
  'orders.hint': 'Every buyer who taps "Order on WhatsApp" appears here. Their message has the same reference (Ref).',
  'orders.status.new': 'New',
  'orders.status.confirmed': 'Confirmed',
  'orders.status.delivered': 'Delivered',
  'orders.status.cancelled': 'Cancelled',
  'orders.sales': 'Sales (30 days)',
  'orders.action.confirmed': 'Confirm',
  'orders.action.delivered': 'Mark delivered',
  'orders.action.cancelled': 'Cancel',
  'orders.action.new': 'Reopen',
  'orders.reference': 'Reference {reference}',
  'orders.withLook': 'Sent their look (see Looks)',
  'orders.empty.title': 'No orders yet',
  'orders.empty.text': 'Share your product links: each tap on "Order on WhatsApp" shows up here.',
  'videos.mode': 'Kind of video',
  'videos.mode.ai': 'AI video 5 s',
  'videos.mode.aiHint': 'The photo comes to life',
  'videos.mode.montage': 'Montage 15 s',
  'videos.mode.montageHint': '2 to 4 photos, free',
  'videos.step.music3': '3. Choose music',
  'videos.step.format4': '4. Choose a format',
  'videos.music.none': 'No music',
  'videos.music.none.hint': 'Add your own sound in TikTok',
  'videos.music.afro': 'Afro',
  'videos.music.afro.hint': 'Upbeat percussion',
  'videos.music.chill': 'Soft',
  'videos.music.chill.hint': 'Calm and warm',
  'videos.music.pop': 'Pop',
  'videos.music.pop.hint': 'Fast and punchy',
  'montage.step.product': '1. Choose the item',
  'montage.step.photos': 'Photos ({count} chosen, 2 to 4)',
  'montage.photosHint': 'Tap to add or remove. The numbers give the order.',
  'montage.photoLabel': 'Worn photo {position}',
  'montage.step.music': 'Music',
  'montage.step.format': 'Format',
  'montage.create': 'Create the montage',
  'montage.free': 'Free: made on our servers, no credits.',
  'montage.working': 'Your montage is being made: about 1 minute.',
  'montage.failed': 'The montage could not be made. Try again or choose other photos.',
  'montage.needTwo': 'A montage needs at least 2 worn photos of the same item. Create another worn photo of it in Catalogue (for example with another model).',
  'ai.describe': 'Write with AI',
  'ai.rewrite': 'Rewrite with AI',
  'ai.writing': 'Writing…',
  'ai.describeHint': 'AI looks at your photo and details. Check the text before publishing.',
  'ai.caption': 'Caption with AI',
  'ai.captionFor': 'Caption for',
  'ai.captionHint': 'Edit it as you like: Share and Copy caption use this text.',
  'ai.channel.whatsapp': 'WhatsApp',
  'ai.channel.tiktok': 'TikTok',
  'ai.channel.instagram': 'Instagram',
  'ai.channel.facebook': 'Facebook',
  'ai.captionLabel': 'Caption',
  'studioError.ai_unavailable': 'AI writing is not available right now. Try again in a moment.',
  'studioError.ai_rejected': 'The AI could not write about this item. Write it yourself or try another photo.',
  'promo.was': 'was',
  'promo.until': 'Offer until {date}',
  'promo.open': 'Start a promotion',
  'promo.title': 'Promotion',
  'promo.discount': 'Discount',
  'promo.customPrice': 'Other price',
  'promo.salePrice': 'Sale price ({currency})',
  'promo.preview': 'Buyers will see {price} instead of {regular}.',
  'promo.duration': 'For how long?',
  'promo.day': '{count} day',
  'promo.days': '{count} days',
  'promo.noEnd': 'Until I stop it',
  'promo.start': 'Start the promotion',
  'promo.stop': 'Stop the promotion',
  'promo.active': 'Promotion −{percent}%: {price}',
  'promo.needPrice': 'Add a price to this item to run a promotion.',
  'studioError.promotion_not_lower': 'The sale price must be lower than the regular price.',
  'studioError.promotion_needs_price': 'Add a price to this item first.',
  'studioError.invalid_promotion_days': 'A promotion lasts 1 to 30 days.',
  'delivery.title': 'Delivery',
  'delivery.free': 'free',
  'whatsapp.delivery': 'Delivery: {zone} ({fee})',
  'whatsapp.total': 'Total: {total}',
  'delivery.studio.title': 'Delivery',
  'delivery.studio.hint': 'List where you deliver and the fee. Buyers choose one; it goes into their WhatsApp message with the total. Leave the fee empty when it is free (e.g. pickup).',
  'delivery.studio.zone': 'Zone {number}',
  'delivery.studio.fee': 'Fee for zone {number} ({currency})',
  'delivery.studio.namePlaceholder': 'e.g. Akwa, Bonamoussadi, Pickup',
  'delivery.studio.feePlaceholder': 'Fee',
  'delivery.studio.remove': 'Remove {name}',
  'delivery.studio.add': 'Add a zone',
  'delivery.studio.save': 'Save delivery',
  'delivery.studio.saved': 'Saved',
  'studioError.invalid_zone_name': 'Each zone needs a name of up to 40 characters.',
  'studioError.invalid_zone_fee': 'Check the fees: numbers only, e.g. 1000.',
  'studioError.duplicate_zone': 'Two zones have the same name.',
  'studioError.too_many_zones': 'At most 20 delivery zones.',
  'replies.default.1.title': 'Available',
  'replies.default.1.body': 'Hello 👋 Thanks for your order ({ref}). "{item}" {size} is available ✅ Total: {total}. Would you rather pay by Mobile Money or on delivery?',
  'replies.default.2.title': 'Payment received',
  'replies.default.2.body': 'Payment received for {ref} 🙏 We are preparing your order "{item}". Delivery: {delivery}.',
  'replies.default.3.title': 'On the way',
  'replies.default.3.body': 'Your order {ref} is on its way 🚚 The rider will call you soon.',
  'replies.default.4.title': 'Sold out',
  'replies.default.4.body': 'Sorry, "{item}" {size} is no longer available 😔 I will let you know when it is back. Would you like to see other styles?',
  'replies.default.5.title': 'Thank you',
  'replies.default.5.body': 'Thank you for shopping at {shop}! Send me a photo when you wear it 😍',
  'replies.open': 'Reply',
  'replies.copied': 'Copied: paste it in the WhatsApp chat.',
  'replies.size': 'size {size}',
  'replies.toAgree': 'to be agreed',
  'replies.edit': 'Edit my quick replies',
  'replies.editHint': 'These texts fill in for each order: {ref} reference, {item} item, {size} size, {total} total, {delivery} delivery, {shop} your shop.',
  'replies.titleLabel': 'Name of reply {number}',
  'replies.bodyLabel': 'Text of reply {number}',
  'replies.add': 'Add a reply',
  'replies.remove': 'Remove "{title}"',
  'replies.save': 'Save replies',
  'replies.saved': 'Saved',
  'replies.reset': 'Back to the default replies',
  'studioError.invalid_reply': 'Each reply needs a name (up to 40 characters) and a text (up to 600).',
  'studioError.too_many_replies': 'At most 12 quick replies.',
  'settings.title': 'Shop settings',
  'settings.back': 'Back to the Studio',
  'settings.saved': 'Shop details saved.',
  'settings.nav.shop': 'Shop',
  'settings.nav.tryOn': 'Buyer try-on',
  'settings.nav.delivery': 'Delivery',
  'settings.nav.replies': 'Quick replies',
  'settings.nav.danger': 'Delete the shop',
  'settings.tryOnOff': 'Buyer try-on is off.',
  'settings.change': 'Change',
  'productForm.shoesHint': 'Shoes: buyers and your models need a head-to-toe photo with the feet visible.',
  'productForm.autoHint': 'The AI detects the type of garment. Choose a precise category when you can: results are better.',
  'nav.create': 'Create',
  'nav.catalog': 'Catalogue',
  'studio.greeting': 'Hello {name} ✨',
  'usage.stills': 'Worn photos (24 h)',
  'usage.videos': 'Status videos (24 h)',
  'usage.left': '{count} left',
  'usage.hint': 'Daily allowance during the pilot. It frees up 24 hours after each creation.',
  'create.step1': 'Garment photo',
  'create.step2': 'Who wears it?',
  'create.optional': 'Optional',
  'create.noModel': 'Nobody for now',
  'create.step3': 'Details',
  'create.publishAndWear': 'Publish and create the worn photo',
  'create.result': 'Result preview',
  'create.before': 'Garment photo',
  'create.after': 'Worn by {model}',
  'create.working': 'Creating the worn photo… about 30 s',
  'create.copyLink': 'Copy try-on link',
  'create.viewCatalog': 'Go to my catalogue',
  'tip.title': 'Tip',
  'tip.body': 'Post your Status image with the try-on link in the caption: buyers can try the item in one tap.',
  'nav.signIn': 'Sign in',
  'product.privacyPill': 'Private photo · erased within 24 h',
  'product.about': 'About this item',
  'product.priceLabel': 'Price',
  'result.askFriend': 'Ask a friend',
  'result.save': 'Save',
  'result.shareText': 'What do you think of {title} on me?',
  'home.hero.before': 'Hanger photo',
  'home.hero.after': 'WhatsApp ready',
  'home.hero.magic': 'Ready to try. Ready to share.',
  'home.hero.caption': 'Real TryOnMe result from one hanger photo',
  'home.hero.credit': 'Model and garment photos are AI-generated examples.',
  'home.hero.alt': 'The same wax jacket worn by a smiling woman: real TryOnMe result',
  'home.trust.fast.title': 'Ready in under a minute',
  'home.trust.fast.body': 'Your share link is created with the product.',
  'home.trust.private.title': 'Buyer photos stay private',
  'home.trust.private.body': 'Never public, erased automatically within 24 hours.',
  'home.trust.whatsapp.title': 'Order on WhatsApp in one tap',
  'home.trust.whatsapp.body': "The product and the buyer's look are pre-filled in the chat.",
  'home.how.eyebrow': 'Simple process',
  'home.how.title': 'How it works',
  'home.how.subtitle': 'Sell more in 3 simple steps from your phone.',
  'home.step': 'Step {n}',
  'home.step1.extra': 'Works with your phone camera',
  'home.step2.extra': 'Auto-formatted in 9:16 for Status',
  'home.step3.extra': 'Their look is attached to the message',
  'home.sides.title': 'Built for both sides of the chat',
  'home.sides.subtitle': 'From the shared link to the WhatsApp order.',
  'home.sellers.title': 'For sellers',
  'home.sellers.tag': 'More orders',
  'home.sellers.1': 'Share one link on your WhatsApp Status, TikTok or Instagram',
  'home.sellers.2': 'Fewer “will it suit me?” questions, more ready-to-buy messages',
  'home.sellers.3': 'Status images and videos with your shop name and price',
  'home.sellers.4': 'See views, try-ons and WhatsApp taps for each product',
  'home.buyers.title': 'For buyers',
  'home.buyers.tag': 'No hesitation',
  'home.buyers.1': 'See the outfit on your own photo before you order',
  'home.buyers.2': 'No app to install: it opens in your browser',
  'home.buyers.3': 'Your photo stays private and is erased within 24 hours',
  'home.buyers.4': 'Order on WhatsApp with your look attached',
  'home.status.eyebrow': 'Status preview',
  'home.status.title': 'WhatsApp Status ready',
  'home.status.subtitle': 'Example 9:16 images made by TryOnMe from the try-on above, in French and English.',
  'home.status.alt': 'Example WhatsApp Status image made by TryOnMe',
  'home.facts.time': 'per try-on',
  'home.facts.apps': 'app to install',
  'home.facts.privacy': 'max. storage of buyer photos',
  'home.facts.languages': 'languages',
  'home.faq.eyebrow': 'Answers',
  'home.faq.title': 'Frequently asked questions',
  'home.faq.q1': 'Is it free to start?',
  'home.faq.a1': 'Yes. TryOnMe is free during the pilot in Douala and Yaoundé: every shop starts with free credits (buyer try-ons, worn photos, Status videos) and top-ups are free on request.',
  'home.faq.q2': 'Do my buyers need an app?',
  'home.faq.a2': 'No. Buyers tap your link on WhatsApp, TikTok or Instagram and everything opens in their browser.',
  'home.faq.q3': 'What happens to buyer photos?',
  'home.faq.a3': 'They stay private: only the buyer sees their result. Selfies are erased automatically within 24 hours, and buyers can delete a try-on at any time. The seller sees a look only if the buyer chooses to send it.',
  'home.faq.q4': 'Which clothes work best?',
  'home.faq.a4': 'One garment per photo, front-facing, on a hanger, a mannequin or laid flat on a plain background: dresses, kaba, wax sets, shirts, jackets and trousers. You can crop out other items in the app.',
  'home.cta.badge': 'Made for African sellers',
  'home.cta.title': 'Start selling with try-on today',
  'home.cta.body': 'Starting in Douala and Yaoundé, built for social sellers across Africa.',
  'home.footer.made': 'Made with pride for African fashion sellers',
  'home.footer.place': 'Douala & Yaoundé, Cameroon',
  'shop.edit': 'Edit shop',
  'result.delete': 'Delete this try-on',
  'result.deleteConfirm': 'Delete your photo and this result? If you sent your look, the seller will no longer see it.',
  'result.deleteYes': 'Delete',
  'result.deleted': 'Try-on deleted: your photo and the result have been erased.',
  'result.deleteError': "We couldn't delete it right now. Please try again.",
  'selfie.retention': 'It is erased automatically within 24 hours.',
  'share.active': 'Link active',
  'share.paused': 'Link paused',
  'share.pausedHint': 'Buyers see “no longer available” until you turn it back on. The link stays the same.',
  'share.toggleError': "We couldn't update the link. Please try again.",
  'still.history': 'Your worn photos',
  'still.another': 'New worn photo with',
  'status.image.refresh': 'Update the Status image',
  'status.image.refreshHint': 'Uses your current shop name, language, title and price. Free.',
  'shop.update': 'Save changes',
  'shop.languageHint': 'Used on your Status images and WhatsApp messages.',
  'photo.lowResPerson': 'Small photo ({width}×{height} px): worn photos come out at this size, so they will be blurry. Use the original camera photo, not a screenshot or a WhatsApp copy.',
  'photo.lowResGarment': 'Very small photo ({width}×{height} px): fabric details may be lost. Ask your supplier for a bigger version if you can.',
  'products.title': 'Your products',
  'products.empty': 'No products yet. Add your first one!',
  'products.add': 'Add a product',
  'productForm.title': 'New product',
  'productForm.photo': 'Product photo',
  'productForm.photoHint':
    'One item only, front-facing, plain background (hanger, mannequin or laid flat). No collage, no extra photos, no accessories.',
  'productForm.name': 'Product name',
  'productForm.category': 'Type',
  'productForm.price': 'Price ({currency})',
  'productForm.description': 'Description (optional)',
  'productForm.publish': 'Publish',
  'productForm.publishing': 'Publishing…',
  'productForm.cancel': 'Cancel',
  'category.full_body': 'Dress / full outfit',
  'category.upper_body': 'Top / shirt',
  'category.outer': 'Coat / jacket / vest',
  'category.shoes': 'Shoes / sandals',
  'category.auto': 'Other (AI decides)',
  'category.lower_body': 'Trousers / skirt (photo worn by someone)',
  'share.copy': 'Copy link',
  'share.copied': 'Link copied!',
  'share.share': 'Share',
  'share.whatsapp': 'Share on WhatsApp',
  'share.message': 'Try "{title}" on yourself before ordering 👉 {url}',
  'share.unavailable': 'Share link managed by TryOnMe',
  'models.title': 'Your models',
  'models.empty': 'Add yourself ("Me") or someone who agreed, to create worn photos.',
  'models.add': 'Add a model',
  'models.kind': 'Who is in the photo?',
  'models.kind.self': 'Me',
  'models.kind.other': 'Another person',
  'models.name': 'Name',
  'models.photo': 'Photo (standing, facing the camera, filling the frame from head to hips)',
  'models.consentTitle': 'Consent',
  'models.consent.self':
    'I agree that TryOnMe generates images of me wearing my products, for my shop and its social posts.',
  'models.consent.other':
    'This person agreed that TryOnMe generates images of them wearing my products, for my shop and its social posts.',
  'models.save': 'Save model',
  'models.saving': 'Saving…',
  'models.noMarketing': 'Consent does not cover marketing',
  'still.create': 'Create worn photo',
  'still.chooseModel': 'Model',
  'still.generate': 'Generate',
  'still.needModel': 'Add a model first (in "Your models").',
  'still.working': 'Creating the worn photo…',
  'still.done': 'Worn photo ready',
  'still.open': 'Open photo',
  'still.alt': '{title} worn by {model}',
  'studioError.consent_required': 'Please confirm the consent statement.',
  'studioError.marketing_consent_required': "This model's consent does not cover marketing photos.",
  'studioError.daily_limit_reached': "You reached today's limit of worn photos. Try again tomorrow.",
  'studioError.demo_limit_reached': 'The demo has reached its generation limit for now.',
  'studioError.generation_failed': "We couldn't create this photo. Try another model photo or product photo.",
  'status.title': 'For your WhatsApp Status and TikTok',
  'status.image.create': 'Create Status image',
  'status.video.create': 'Create Status video',
  'status.video.hint': 'Takes about a minute.',
  'status.working': 'Creating…',
  'status.share': 'Share',
  'status.download': 'Download',
  'status.copyCaption': 'Copy caption',
  'status.captionCopied': 'Caption copied!',
  'status.imageAlt': 'Status image of {title}',
  'studioError.social_consent_required': "This model's consent does not cover social posts.",
  'studioError.composition_failed': "We couldn't create this media. Please try again.",
  'crop.frame': 'Crop frame. Use arrow keys to move it, Shift + arrows to resize.',
  'crop.reset': 'Reset frame',
  'crop.skip': 'Skip',
  'crop.apply': 'Use this crop',
  'crop.edit': 'Crop',
  'crop.productHint': 'Frame a single item. Leave out collages, inset photos and accessories.',
  'crop.modelHint': 'Match the silhouette: head at the top of the frame, hips at the bottom.',
  'photo.choose': 'Choose a photo',
  'photo.change': 'Change photo',
  'photo.formats': 'JPG or PNG, up to 10 MB',
  'home.badge': 'Virtual try-on for social sellers',
  'home.step1.title': 'Snap your item',
  'home.step1.body': 'On a hanger or laid flat. Crop out the rest in one gesture.',
  'home.step2.title': 'Get a worn photo and Status',
  'home.step2.body': 'Your item worn by you or your model, branded for WhatsApp Status and TikTok.',
  'home.step3.title': 'Buyers try it, then order',
  'home.step3.body': 'They see it on themselves and message you on WhatsApp, look included.',
  'home.tile1': 'Hanger photo',
  'home.tile2': 'Worn, ready for Status',
  'studio.tabs.products': 'Products',
  'studio.tabs.models': 'Models',
  'studio.tabs.looks': 'Looks',
  'studio.stats.views': 'Views',
  'studio.stats.tryOns': 'Try-ons',
  'studio.stats.whatsapp': 'WhatsApp',
  'studio.stats.looks': 'Looks',
  'studio.productsHint': 'Share a link: your buyers try the item on before ordering.',
  'studio.modelsHint': 'Who wears your items in your worn photos and Status posts.',
  'studio.looksHint': 'Looks your buyers chose to send you.',
  'product.trust.private': 'Your photo stays private',
  'product.trust.ai': 'AI preview',
  'progress.title': 'Your try-on is on its way',
  'selfie.tip.facing': 'Facing the camera, standing',
  'selfie.tip.frame': 'Fill the frame from head to hips',
  'selfie.tip.frameFeet': 'Head to toe: your feet must be visible',
  'selfie.tip.light': 'Good light, plain background',
  'selfie.tip.alone': 'Alone in the photo',
  'studioError.invalid_shop_name': 'Enter a shop name (80 characters max).',
  'studioError.invalid_whatsapp': "This WhatsApp number doesn't look right for the chosen country.",
  'studioError.unsupported_country': 'This country is not supported yet.',
  'studioError.invalid_title': 'Enter a product name (120 characters max).',
  'studioError.invalid_price': 'Enter a valid price, for example 15000.',
  'studioError.image_too_small': 'This photo is too small. Use a larger, sharper photo.',
  'studioError.unsupported_image': 'Use a JPG or PNG photo.',
  'studioError.image_too_large': 'This photo is too large (10 MB max).',
  'studioError.missing_photo': 'Add a photo of the product.',
  'studioError.network': 'No connection. Check your internet and try again.',
  'studioError.generic': 'Something went wrong. Please try again.',
  'notFound.title': 'Page not found',
  'notFound.back': 'Back to home',
} as const

export type MessageKey = keyof typeof en
export type Messages = Record<MessageKey, string>

const fr: Messages = {
  'try.title': 'Vois le vêtement sur toi avant de commander.',
  'try.intro': 'Ouvre le lien produit de ton vendeur pour essayer le vêtement sur toi.',
  'try.link': 'Lien du produit',
  'try.hint': 'Demande le lien produit TryOnMe à ton vendeur, ou copie-le depuis sa publication.',
  'try.invalid': 'Colle un lien produit de ce site TryOnMe. Il doit contenir /p/ suivi du code du produit.',
  'try.open': 'Ouvrir le produit',
  'try.private': 'Pas besoin de compte. Ta photo reste privée.',
  'try.next': 'Trois étapes simples',
  'try.step1': 'Ouvre le produit partagé par ton vendeur.',
  'try.step2': 'Ajoute ta photo et vois le vêtement sur toi.',
  'try.step3': 'Choisis ta taille et écris au vendeur sur WhatsApp.',
  'a11y.skip': 'Aller au contenu',
  'a11y.navigation': 'Navigation principale',
  'a11y.loading': 'Chargement de la page…',
  'catalog.search': 'Rechercher un produit',
  'catalog.clear': 'Effacer la recherche',
  'catalog.count': '{count} produits',
  'catalog.noResults': 'Aucun produit trouvé. Essaie un autre nom.',
  'seo.studio': 'Studio vendeur',
  'seo.look': 'Look partagé privé',
  'seo.poster': 'Imprimer ton affiche boutique',
  'home.shopperLabel': 'Pour les clients',
  'home.sellerLabel': 'Pour les vendeurs',
  'home.shopperBrief': 'Vois les vêtements sur toi.',
  'home.sellerBrief': 'Transforme une photo de produit en essayages et en contenu pour les réseaux sociaux.',
  'app.name': 'TryOnMe',
  'language.label': 'Langue',
  'language.switchTo': 'English',
  'home.shoppers': 'Pour les clients : vois les vêtements sur toi.',
  'home.sellerIntro': 'Pour les vendeurs : transforme une photo de produit en essayages et en contenu pour les réseaux sociaux.',
  'home.title': 'Essaie avant d’acheter.',
  'home.subtitle': "Vends avant de photographier un modèle.",
  'home.createShop': 'Créer du contenu vendeur',
  'home.seeExample': 'Essayer un vêtement',
  'product.loading': 'Chargement du produit…',
  'product.notFound': "Ce produit n'est plus disponible.",
  'product.loadError': 'Impossible de charger ce produit. Vérifie ta connexion et réessaie.',
  'product.retry': 'Réessayer',
  'product.soldBy': 'Vendu par {shop}',
  'tryOn.cta': 'Essayer sur moi',
  'whatsapp.order': 'Commander sur WhatsApp',
  'whatsapp.message': 'Bonjour {shop} 👋 Je veux « {title} » ({price}). Vu ici : {url}',
  'whatsapp.messageNoPrice': 'Bonjour {shop} 👋 Je veux « {title} ». Vu ici : {url}',
  'whatsapp.messageWithLook':
    'Bonjour {shop} 👋 Je veux « {title} » ({price}). Voici comment ça me va : {look}',
  'whatsapp.messageWithLookNoPrice': 'Bonjour {shop} 👋 Je veux « {title} ». Voici comment ça me va : {look}',
  'look.toggle': 'Envoyer mon look au vendeur',
  'look.consent':
    "Seul·e {shop} pourra voir cette image, pendant 30 jours. Ta photo d'origine n'est jamais partagée.",
  'look.sending': 'Préparation de ton look…',
  'look.ready': 'Ton look sera ajouté à ton message WhatsApp.',
  'look.error': "Impossible de partager ton look. Tu peux quand même commander sur WhatsApp.",
  'looks.title': 'Les looks de tes clients',
  'looks.empty': 'Quand un client t’envoie son look, il apparaît ici.',
  'looks.sharedOn': 'Envoyé le {date}',
  'looks.expired': "Ce look n'est plus disponible (retiré par le client ou expiré).",
  'looks.signIn': 'Connecte-toi pour voir ce look.',
  'stats.views': '{count} vues',
  'stats.tryOns': '{count} essayages',
  'stats.whatsapp': '{count} WhatsApp',
  'stats.looks': '{count} looks',
  'disclosure.ai': "Aperçu généré par IA. Ce n'est pas une garantie de taille.",
  'selfie.title': 'Ta photo',
  'selfie.guidance':
    "Mets-toi face à l'appareil, seul·e, avec une bonne lumière. Remplis l'image de la tête aux hanches, avec peu d'espace vide autour.",
  'selfie.camera': 'Prendre une photo',
  'selfie.gallery': 'Choisir dans la galerie',
  'selfie.privacy': 'Ta photo reste privée.',
  'selfie.back': 'Retour',
  'progress.preparing': 'Préparation de ta photo',
  'progress.creating': 'Création de ton essayage',
  'progress.finishing': 'Presque fini',
  'result.title': 'Voici comment ça te va',
  'result.alt': "Aperçu de l'essayage : {title}",
  'result.another': 'Essayer une autre photo',
  'error.person_not_detected':
    'On ne te voit pas bien. Prends une photo de face, visage et épaules visibles.',
  'error.multiple_people': 'Une seule personne doit être sur la photo.',
  'error.image_too_small': 'Cette photo est trop petite. Utilise une photo plus grande et nette.',
  'error.unsupported_image': 'Utilise une photo JPG ou PNG.',
  'error.image_too_large': 'Cette photo est trop lourde (10 Mo max).',
  'error.garment_unsuitable':
    'Cet article ne peut pas encore être essayé. Écris au vendeur sur WhatsApp.',
  'error.content_rejected': "Cette photo ne peut pas être utilisée. Essaie-en une autre.",
  'error.temporary': 'Un souci de notre côté. Réessaie dans un instant.',
  'error.demo_limit_reached':
    "La démo a atteint sa limite d'essais pour le moment. Contacte quand même le vendeur sur WhatsApp !",
  'error.network': 'Pas de connexion. Vérifie ton internet et réessaie.',
  'studio.title': 'Studio',
  'studio.loading': 'Chargement de ta boutique…',
  'studio.signIn.title': "Vends plus grâce à l'essayage",
  'studio.signIn.body':
    'Connecte-toi pour créer ta boutique et partager des produits que tes clients peuvent essayer avant de commander.',
  'studio.signIn.google': 'Continuer avec Google',
  'studio.signIn.failed': "La connexion n'a pas marché. Réessaie.",
  'studio.signIn.emailNotVerified': 'Ton adresse email Google doit être vérifiée.',
  'studio.logout': 'Se déconnecter',
  'shop.title': 'Ta boutique',
  'shop.name': 'Nom de la boutique',
  'shop.country': 'Pays',
  'shop.whatsapp': 'Numéro WhatsApp',
  'shop.whatsappHint': "Tes clients t'écriront sur ce numéro.",
  'shop.language': 'Langue de tes messages',
  'shop.save': 'Créer ma boutique',
  'shop.saving': 'Enregistrement…',
  'product.edit': 'Modifier le produit',
  'product.editShort': 'Modifier',
  'product.replacePhoto': 'Remplacer la photo (facultatif)',
  'product.replacePhotoHint': 'Laisse vide pour garder la photo actuelle. Ton lien de partage reste le même.',
  'product.delete': 'Supprimer le produit',
  'product.deleteConfirm': 'Supprimer « {title} » ? Son lien ne marchera plus, et sa photo, ses photos portées et ses images Statut seront effacées.',
  'models.delete': 'Supprimer',
  'models.deleteConfirm': 'Supprimer {name} ? Son consentement est retiré, et sa photo ainsi que toutes les images créées avec cette personne sont effacées.',
  'studioError.generation_in_progress': 'Une création avec cet élément est en cours. Réessaie dans une minute.',
  'admin.eyebrow': 'Opérateur',
  'admin.title': 'Tableau de bord admin',
  'admin.updated': 'Mis à jour à {time}',
  'admin.refresh': 'Actualiser',
  'admin.last7': '{count} sur les 7 derniers jours',
  'admin.kpi.sellers': 'Vendeurs',
  'admin.kpi.shops': 'Boutiques',
  'admin.kpi.products': 'Produits',
  'admin.kpi.models': 'Modèles',
  'admin.kpi.tryOns': 'Essayages clients',
  'admin.kpi.stills': 'Photos portées',
  'admin.kpi.statusImages': 'Images Statut',
  'admin.kpi.videos': 'Vidéos Statut',
  'admin.kpi.looks': 'Looks actifs',
  'admin.kpi.views': 'Vues (7 j)',
  'admin.kpi.whatsapp': 'Clics WhatsApp (7 j)',
  'admin.units.title': 'Unités YouCam',
  'admin.units.committed': 'Engagées',
  'admin.units.left': 'restantes avant le plafond',
  'admin.units.reserve': '{count} unités en fin de plafond sont réservées aux vendeurs et à la démo (les clients s’arrêtent avant).',
  'admin.units.used24h': 'utilisées en 24 h',
  'admin.units.used7d': 'utilisées en 7 jours',
  'admin.daily.title': '14 derniers jours',
  'admin.op.clothing_try_on': 'Essayage client',
  'admin.op.seller_try_on_still': 'Photo portée',
  'admin.op.seller_promo_video': 'Vidéo Statut',
  'admin.jobs.title': 'Résultats des tâches (7 jours)',
  'admin.failures.title': 'Causes d’échec (7 jours)',
  'admin.failures.none': 'Aucun échec. 🎉',
  'admin.empty': 'Rien pour l’instant.',
  'admin.recent.title': 'Tâches récentes',
  'admin.shops.title': 'Boutiques',
  'admin.buyer': 'Client',
  'admin.col.when': 'Quand',
  'admin.col.operation': 'Opération',
  'admin.col.status': 'Statut',
  'admin.col.shop': 'Boutique',
  'admin.col.duration': 'Durée',
  'admin.col.units': 'Unités',
  'admin.col.error': 'Erreur',
  'admin.col.since': 'Depuis',
  'admin.link': 'Admin',
  'admin.nav.overview': 'Vue d’ensemble',
  'admin.nav.jobs': 'Tâches',
  'admin.nav.shops': 'Boutiques',
  'admin.nav.studio': 'Retour au Studio',
  'admin.nav.collapse': 'Réduire le menu',
  'admin.nav.expand': 'Déployer le menu',
  'admin.nav.open': 'Ouvrir le menu',
  'admin.nav.close': 'Fermer le menu',
  'error.photo_expired': 'Ta photo précédente a été effacée (on garde les photos 24 h maximum). Prends-en une nouvelle.',
  'selfie.reuse': 'Utiliser ma photo précédente',
  'selfie.reuseHint': 'La photo utilisée pour un autre article. Elle reste privée.',
  'selfie.or': 'ou une nouvelle photo',
  'shop.page.tagline': 'Essaie chaque article sur toi avant de commander.',
  'shop.page.empty': 'Aucun article disponible pour le moment. Reviens bientôt !',
  'shop.page.notFound': 'Cette boutique n’existe pas.',
  'shop.page.tryOn': 'Essayer',
  'shop.page.contact': 'Écrire sur WhatsApp',
  'shop.page.message': 'Bonjour {shop} 👋 J’ai vu ta boutique sur TryOnMe : {url}',
  'shop.page.seeAll': 'Voir toute la boutique',
  'shop.page.tryAnother': 'Essayer un autre article',
  'shop.link.title': 'Ta boutique en ligne',
  'shop.link.hint': 'Un seul lien pour tout ton catalogue : mets-le dans ton Statut WhatsApp, ta bio TikTok ou Instagram.',
  'checklist.title': 'Pour bien démarrer',
  'checklist.progress': '{done} étapes sur {total}',
  'checklist.model': 'Ajoute-toi comme modèle',
  'checklist.product': 'Ajoute ton premier produit',
  'checklist.still': 'Crée une photo portée',
  'checklist.share': 'Partage le lien de ta boutique',
  'checklist.tryOn': 'Ton premier client essaie un article',
  'checklist.tryOnHint': 'Se valide dès qu’un client essaie un article depuis ton lien.',
  'checklist.copyLink': 'Copier mon lien',
  'checklist.go': 'Commencer',
  'checklist.hide': 'Masquer la liste',
  'checklist.done': '(fait)',
  'checklist.todo': '(à faire)',
  'poster.kicker': 'Essaie avant d’acheter',
  'poster.shopTagline': 'Essaie chaque article sur toi, puis commande sur WhatsApp.',
  'poster.scanShop': 'Scanne pour voir la boutique et essayer sur toi',
  'poster.scanProduct': 'Scanne pour l’essayer sur toi',
  'poster.qrAlt': 'QR code menant à {url}',
  'poster.footer': 'Ta photo reste privée · TryOnMe',
  'poster.print': 'Imprimer',
  'poster.open': 'Affiche QR',
  'legal.privacy': 'Confidentialité',
  'legal.terms': 'Conditions',
  'legal.updated': 'Dernière mise à jour : {date}',
  'legal.contact': 'Contact :',
  'legal.signInPrefix': 'En continuant, tu acceptes les',
  'legal.and': 'et la politique de',
  'deleteShop.title': 'Supprimer ma boutique et mon compte',
  'deleteShop.body': 'Tes produits, modèles, photos portées et médias Statut sont effacés, tes liens ne marchent plus et tu es déconnecté·e. C’est définitif.',
  'deleteShop.start': 'Supprimer ma boutique…',
  'deleteShop.confirmLabel': 'Tape « {name} » pour confirmer',
  'deleteShop.mismatch': 'Le nom ne correspond pas à celui de ta boutique.',
  'deleteShop.confirm': 'Supprimer définitivement',
  'deleteShop.deleting': 'Suppression…',
  'looks.new': 'Nouveau',
  'looks.newBannerOne': 'Un client t’a envoyé un nouveau look',
  'looks.newBannerMany': 'Tu as {count} nouveaux looks de clients',
  'looks.newCta': 'Voir',
  'credits.label': 'crédits',
  'credits.history': 'Historique',
  'credits.hideHistory': 'Masquer l’historique',
  'credits.noHistory': 'Aucun mouvement de crédits pour l’instant.',
  'credits.prices': 'Essayage client : {tryOn} · photo portée : {still} · fond propre : {cleanup} · vidéo : {video} · image Statut : gratuite',
  'credits.low': 'Tes crédits commencent à manquer.',
  'credits.empty': 'Tu n’as plus de crédits : tes clients ne peuvent plus essayer tes articles.',
  'credits.topUp': 'Demander une recharge',
  'credits.topUpSubject': 'Recharge de crédits TryOnMe',
  'credits.costOne': '1 crédit',
  'credits.costMany': '{count} crédits',
  'credits.balanceShort': 'solde : {count}',
  'credits.notEnough': 'crédits insuffisants',
  'credits.kind.welcome': 'Crédits de bienvenue',
  'credits.kind.grant': 'Recharge',
  'credits.kind.try_on': 'Essayage client',
  'credits.kind.still': 'Photo portée',
  'credits.kind.video': 'Vidéo Statut',
  'credits.kind.refund': 'Remboursement (création échouée)',
  'studioError.insufficient_credits': 'Pas assez de crédits pour cela. Demande une recharge.',
  'error.try_on_unavailable': 'L’essayage n’est pas disponible pour cet article pour le moment. Tu peux quand même commander sur WhatsApp.',
  'admin.col.credits': 'Crédits',
  'admin.grant.add': 'Ajouter',
  'admin.grant.amount': 'Crédits à ajouter',
  'admin.grant.note': 'Motif',
  'admin.grant.done': 'Nouveau solde : {count}',
  'admin.nav.credits': 'Crédits',
  'admin.credits.add': 'Ajouter des crédits',
  'admin.credits.balance': 'solde {count}',
  'admin.credits.negativeHint': 'Un montant négatif corrige une erreur (jamais en dessous de 0).',
  'admin.credits.notePlaceholder': 'ex. recharge pilote',
  'admin.credits.review': 'Vérifier',
  'admin.credits.summaryAdd': 'Ajouter {count} crédits à {shop}',
  'admin.credits.summaryRemove': 'Retirer {count} crédits à {shop}',
  'admin.credits.wouldBeNegative': 'Le solde ne peut pas passer sous 0.',
  'admin.credits.confirm': 'Confirmer',
  'admin.credits.working': 'Enregistrement…',
  'admin.credits.edit': 'Modifier',
  'admin.credits.done': '{shop} : nouveau solde {count}.',
  'admin.credits.recent': 'Recharges récentes',
  'tryOnSwitch.label': 'Essayage pour mes clients',
  'tryOnSwitch.onHint': 'Tes clients peuvent essayer tes articles (cela utilise tes crédits).',
  'tryOnSwitch.offHint': 'Désactivé : tes clients voient tes articles et commandent sur WhatsApp, sans essayage. Aucun crédit n’est utilisé.',
  'shop.page.taglineNoTryOn': 'Découvre les articles et commande sur WhatsApp.',
  'poster.scanShopNoTryOn': 'Scanne pour voir la boutique',
  'poster.scanProductNoTryOn': 'Scanne pour voir l’article',
  'nav.videos': 'Vidéos',
  'videos.title': 'Vidéos',
  'videos.hint': 'Pour TikTok, Reels, Statut WhatsApp et Facebook. Choisis une photo, un style et un format.',
  'videos.step.photo': '1. Choisis une photo portée',
  'videos.step.style': '2. Choisis un style',
  'videos.step.format': '3. Choisis un format',
  'videos.style.runway': 'Défilé',
  'videos.style.runway.hint': 'Mouvement naturel et pose',
  'videos.style.zoom': 'Zoom',
  'videos.style.zoom.hint': 'Zoom lent sur la tenue',
  'videos.style.turn': 'Rotation',
  'videos.style.turn.hint': 'Le modèle se tourne pour la montrer',
  'videos.format.vertical': 'Vertical 9:16',
  'videos.format.vertical.hint': 'TikTok, Reels, Statut WhatsApp, Shorts',
  'videos.format.square': 'Carré 1:1',
  'videos.format.square.hint': 'Fil Facebook et Instagram',
  'videos.create': 'Créer la vidéo',
  'videos.working': 'Ta vidéo se prépare : 1 à 2 minutes. Reste sur cette page.',
  'videos.failed': 'La vidéo n’a pas pu être créée. Tes crédits ont été remboursés : essaie une autre photo ou un autre style.',
  'videos.ready': 'Nouveau',
  'videos.mine': 'Mes vidéos',
  'videos.pending': '{count} vidéo(s) encore en préparation. Reviens dans une minute.',
  'videos.noPhotos.title': 'Commence par une photo portée',
  'videos.noPhotos.text': 'Une vidéo part d’une photo portée. Dans Catalogue, ouvre un article et touche « Créer la photo portée ».',
  'videos.noPhotos.action': 'Aller au catalogue',
  'credits.kind.cleanup': 'Fond propre',
  'clean.open': 'Fond propre',
  'clean.title': 'Fond propre',
  'clean.hint': 'Retire le lit, le mur ou le sol derrière ton article et le pose sur un fond uni. Ta photo actuelle reste tant que tu ne choisis pas.',
  'clean.backdrop': 'Fond',
  'clean.backdrop.white': 'Blanc',
  'clean.backdrop.grey': 'Gris clair',
  'clean.backdrop.cream': 'Crème',
  'clean.create': 'Nettoyer la photo',
  'clean.working': 'Nettoyage…',
  'clean.wait': 'Environ 30 secondes.',
  'clean.before': 'Avant',
  'clean.after': 'Après',
  'clean.beforeAlt': 'Photo actuelle de {title}',
  'clean.afterAlt': 'Photo nettoyée de {title}',
  'clean.use': 'Utiliser cette photo',
  'clean.again': 'Essayer un autre fond',
  'clean.useHint': 'Ton lien, tes compteurs et tes photos portées restent les mêmes.',
  'clean.failed': 'On n’a pas pu séparer l’article du fond. Tes crédits ont été remboursés : essaie une photo où l’article est bien visible.',
  'clean.close': 'Fermer',
  'product.size': 'Taille',
  'product.sizeHint': 'Choisis ta taille : elle sera dans ton message WhatsApp.',
  'product.soldOut': 'Épuisé',
  'whatsapp.askAvailability': 'Demander s’il revient',
  'whatsapp.stillAvailable': 'Est-il de nouveau disponible ?',
  'whatsapp.size': 'Taille : {size}',
  'whatsapp.reference': 'Réf : {reference}',
  'stock.title': 'Tailles et stock',
  'stock.inStock': 'En stock',
  'stock.outOfStock': 'En rupture : les clients peuvent seulement demander s’il revient',
  'stock.tapHint': 'Touche une taille pour la marquer épuisée (ou de nouveau disponible).',
  'stock.markSoldOut': '(disponible, touche pour la marquer épuisée)',
  'stock.markAvailable': '(épuisée, touche pour la remettre en stock)',
  'stock.remove': 'Retirer la taille {size}',
  'stock.addSizes': 'Ajouter des tailles',
  'stock.preset.one': 'Taille unique',
  'stock.custom': 'Autres tailles',
  'stock.customPlaceholder': 'ex. 2 ans, 4 ans',
  'stock.add': 'Ajouter',
  'studioError.invalid_size': 'Une taille doit faire de 1 à 16 caractères.',
  'studioError.duplicate_size': 'Cette taille est déjà dans la liste.',
  'studioError.too_many_sizes': '20 tailles maximum par article.',
  'nav.orders': 'Ventes',
  'orders.title': 'Commandes',
  'orders.hint': 'Chaque client qui touche « Commander sur WhatsApp » apparaît ici. Son message porte la même référence (Réf).',
  'orders.status.new': 'Nouvelles',
  'orders.status.confirmed': 'Confirmées',
  'orders.status.delivered': 'Livrées',
  'orders.status.cancelled': 'Annulées',
  'orders.sales': 'Ventes (30 jours)',
  'orders.action.confirmed': 'Confirmer',
  'orders.action.delivered': 'Marquer livrée',
  'orders.action.cancelled': 'Annuler',
  'orders.action.new': 'Rouvrir',
  'orders.reference': 'Référence {reference}',
  'orders.withLook': 'A envoyé son look (voir Looks)',
  'orders.empty.title': 'Pas encore de commande',
  'orders.empty.text': 'Partage tes liens : chaque appui sur « Commander sur WhatsApp » apparaît ici.',
  'videos.mode': 'Type de vidéo',
  'videos.mode.ai': 'Vidéo IA 5 s',
  'videos.mode.aiHint': 'La photo prend vie',
  'videos.mode.montage': 'Montage 15 s',
  'videos.mode.montageHint': '2 à 4 photos, gratuit',
  'videos.step.music3': '3. Choisis une musique',
  'videos.step.format4': '4. Choisis un format',
  'videos.music.none': 'Sans musique',
  'videos.music.none.hint': 'Ajoute ton son dans TikTok',
  'videos.music.afro': 'Afro',
  'videos.music.afro.hint': 'Percussions entraînantes',
  'videos.music.chill': 'Doux',
  'videos.music.chill.hint': 'Calme et chaleureux',
  'videos.music.pop': 'Pop',
  'videos.music.pop.hint': 'Rapide et punchy',
  'montage.step.product': '1. Choisis l’article',
  'montage.step.photos': 'Photos ({count} choisies, de 2 à 4)',
  'montage.photosHint': 'Touche pour ajouter ou retirer. Les numéros donnent l’ordre.',
  'montage.photoLabel': 'Photo portée {position}',
  'montage.step.music': 'Musique',
  'montage.step.format': 'Format',
  'montage.create': 'Créer le montage',
  'montage.free': 'Gratuit : fait sur nos serveurs, sans crédits.',
  'montage.working': 'Ton montage se prépare : environ 1 minute.',
  'montage.failed': 'Le montage n’a pas pu être créé. Réessaie ou choisis d’autres photos.',
  'montage.needTwo': 'Un montage demande au moins 2 photos portées du même article. Crée une autre photo portée dans Catalogue (par exemple avec un autre modèle).',
  'ai.describe': 'Écrire avec l’IA',
  'ai.rewrite': 'Réécrire avec l’IA',
  'ai.writing': 'Écriture…',
  'ai.describeHint': 'L’IA regarde ta photo et tes informations. Relis le texte avant de publier.',
  'ai.caption': 'Légende avec l’IA',
  'ai.captionFor': 'Légende pour',
  'ai.captionHint': 'Modifie-la comme tu veux : Partager et Copier la légende utilisent ce texte.',
  'ai.channel.whatsapp': 'WhatsApp',
  'ai.channel.tiktok': 'TikTok',
  'ai.channel.instagram': 'Instagram',
  'ai.channel.facebook': 'Facebook',
  'ai.captionLabel': 'Légende',
  'studioError.ai_unavailable': 'L’écriture par IA n’est pas disponible pour le moment. Réessaie dans un instant.',
  'studioError.ai_rejected': 'L’IA n’a pas pu écrire sur cet article. Écris-le toi-même ou essaie une autre photo.',
  'promo.was': 'au lieu de',
  'promo.until': 'Offre jusqu’au {date}',
  'promo.open': 'Lancer une promo',
  'promo.title': 'Promotion',
  'promo.discount': 'Réduction',
  'promo.customPrice': 'Autre prix',
  'promo.salePrice': 'Prix promo ({currency})',
  'promo.preview': 'Tes clients verront {price} au lieu de {regular}.',
  'promo.duration': 'Pendant combien de temps ?',
  'promo.day': '{count} jour',
  'promo.days': '{count} jours',
  'promo.noEnd': 'Jusqu’à ce que je l’arrête',
  'promo.start': 'Lancer la promo',
  'promo.stop': 'Arrêter la promo',
  'promo.active': 'Promo −{percent} % : {price}',
  'promo.needPrice': 'Ajoute un prix à cet article pour lancer une promo.',
  'studioError.promotion_not_lower': 'Le prix promo doit être plus bas que le prix normal.',
  'studioError.promotion_needs_price': 'Ajoute d’abord un prix à cet article.',
  'studioError.invalid_promotion_days': 'Une promo dure de 1 à 30 jours.',
  'delivery.title': 'Livraison',
  'delivery.free': 'gratuit',
  'whatsapp.delivery': 'Livraison : {zone} ({fee})',
  'whatsapp.total': 'Total : {total}',
  'delivery.studio.title': 'Livraison',
  'delivery.studio.hint': 'Indique où tu livres et le prix. Le client choisit ; c’est ajouté à son message WhatsApp avec le total. Laisse le prix vide si c’est gratuit (ex. retrait).',
  'delivery.studio.zone': 'Zone {number}',
  'delivery.studio.fee': 'Prix de la zone {number} ({currency})',
  'delivery.studio.namePlaceholder': 'ex. Akwa, Bonamoussadi, Retrait',
  'delivery.studio.feePlaceholder': 'Prix',
  'delivery.studio.remove': 'Retirer {name}',
  'delivery.studio.add': 'Ajouter une zone',
  'delivery.studio.save': 'Enregistrer la livraison',
  'delivery.studio.saved': 'Enregistré',
  'studioError.invalid_zone_name': 'Chaque zone a besoin d’un nom de 40 caractères maximum.',
  'studioError.invalid_zone_fee': 'Vérifie les prix : des chiffres seulement, ex. 1000.',
  'studioError.duplicate_zone': 'Deux zones ont le même nom.',
  'studioError.too_many_zones': '20 zones de livraison maximum.',
  'replies.default.1.title': 'Disponible',
  'replies.default.1.body': 'Bonjour 👋 Merci pour ta commande ({ref}). « {item} » {size} est disponible ✅ Total : {total}. Tu préfères payer par Mobile Money ou à la livraison ?',
  'replies.default.2.title': 'Paiement reçu',
  'replies.default.2.body': 'Paiement bien reçu pour {ref} 🙏 On prépare ta commande « {item} ». Livraison : {delivery}.',
  'replies.default.3.title': 'En route',
  'replies.default.3.body': 'Ta commande {ref} est en route 🚚 Le livreur t’appelle bientôt.',
  'replies.default.4.title': 'Épuisé',
  'replies.default.4.body': 'Désolé, « {item} » {size} n’est plus disponible 😔 Je te préviens dès qu’il revient. Tu veux voir d’autres modèles ?',
  'replies.default.5.title': 'Merci',
  'replies.default.5.body': 'Merci pour ton achat chez {shop} ! Envoie-moi une photo quand tu le portes 😍',
  'replies.open': 'Répondre',
  'replies.copied': 'Copié : colle-le dans la discussion WhatsApp.',
  'replies.size': 'taille {size}',
  'replies.toAgree': 'à convenir',
  'replies.edit': 'Modifier mes réponses rapides',
  'replies.editHint': 'Ces textes se remplissent pour chaque commande : {ref} référence, {item} article, {size} taille, {total} total, {delivery} livraison, {shop} ta boutique.',
  'replies.titleLabel': 'Nom de la réponse {number}',
  'replies.bodyLabel': 'Texte de la réponse {number}',
  'replies.add': 'Ajouter une réponse',
  'replies.remove': 'Retirer « {title} »',
  'replies.save': 'Enregistrer les réponses',
  'replies.saved': 'Enregistré',
  'replies.reset': 'Revenir aux réponses par défaut',
  'studioError.invalid_reply': 'Chaque réponse a besoin d’un nom (40 caractères max.) et d’un texte (600 max.).',
  'studioError.too_many_replies': '12 réponses rapides maximum.',
  'settings.title': 'Réglages de la boutique',
  'settings.back': 'Retour au Studio',
  'settings.saved': 'Infos de la boutique enregistrées.',
  'settings.nav.shop': 'Boutique',
  'settings.nav.tryOn': 'Essayage client',
  'settings.nav.delivery': 'Livraison',
  'settings.nav.replies': 'Réponses rapides',
  'settings.nav.danger': 'Supprimer la boutique',
  'settings.tryOnOff': 'L’essayage client est désactivé.',
  'settings.change': 'Modifier',
  'productForm.shoesHint': 'Chaussures : les clients et tes modèles doivent être pris en photo de la tête aux pieds, pieds visibles.',
  'productForm.autoHint': 'L’IA détecte le type de vêtement. Choisis une catégorie précise quand tu peux : le résultat est meilleur.',
  'nav.create': 'Créer',
  'nav.catalog': 'Catalogue',
  'studio.greeting': 'Bonjour {name} ✨',
  'usage.stills': 'Photos portées (24 h)',
  'usage.videos': 'Vidéos Statut (24 h)',
  'usage.left': '{count} restantes',
  'usage.hint': 'Quota quotidien pendant le pilote. Il se libère 24 h après chaque création.',
  'create.step1': 'Photo du vêtement',
  'create.step2': 'Qui le porte ?',
  'create.optional': 'Optionnel',
  'create.noModel': 'Personne pour l’instant',
  'create.step3': 'Détails',
  'create.publishAndWear': 'Publier et créer la photo portée',
  'create.result': 'Aperçu du résultat',
  'create.before': 'Photo du vêtement',
  'create.after': 'Porté par {model}',
  'create.working': 'Création de la photo portée… environ 30 s',
  'create.copyLink': 'Copier le lien d’essayage',
  'create.viewCatalog': 'Voir mon catalogue',
  'tip.title': 'Conseil',
  'tip.body': 'Publie ton image Statut avec le lien d’essayage en légende : tes clients essaient l’article en un geste.',
  'nav.signIn': 'Se connecter',
  'product.privacyPill': 'Photo privée · effacée sous 24 h',
  'product.about': 'À propos de cet article',
  'product.priceLabel': 'Prix',
  'result.askFriend': 'Avis d’une amie',
  'result.save': 'Sauvegarder',
  'result.shareText': 'Tu en penses quoi, {title} sur moi ?',
  'home.hero.before': 'Photo sur cintre',
  'home.hero.after': 'Prêt pour WhatsApp',
  'home.hero.magic': 'Prêt à essayer. Prêt à partager.',
  'home.hero.caption': 'Vrai résultat TryOnMe à partir d’une photo sur cintre',
  'home.hero.credit': 'Photos du modèle et du vêtement générées par IA, à titre d’exemple.',
  'home.hero.alt': 'La même veste en wax portée par une femme souriante : vrai résultat TryOnMe',
  'home.trust.fast.title': 'Prêt en moins d’une minute',
  'home.trust.fast.body': 'Ton lien de partage est créé avec le produit.',
  'home.trust.private.title': 'Les photos des clients restent privées',
  'home.trust.private.body': 'Jamais publiques, effacées automatiquement sous 24 h.',
  'home.trust.whatsapp.title': 'Commande sur WhatsApp en un geste',
  'home.trust.whatsapp.body': 'Le produit et le look du client sont déjà dans le message.',
  'home.how.eyebrow': 'Simple comme bonjour',
  'home.how.title': 'Comment ça marche',
  'home.how.subtitle': 'Vends plus en 3 étapes depuis ton téléphone.',
  'home.step': 'Étape {n}',
  'home.step1.extra': 'Marche avec l’appareil photo de ton téléphone',
  'home.step2.extra': 'Format 9:16 automatique pour le Statut',
  'home.step3.extra': 'Leur look est joint au message',
  'home.sides.title': 'Pensé pour les deux côtés de la discussion',
  'home.sides.subtitle': 'Du lien partagé à la commande WhatsApp.',
  'home.sellers.title': 'Pour les vendeurs',
  'home.sellers.tag': 'Plus de commandes',
  'home.sellers.1': 'Un seul lien à partager sur ton Statut WhatsApp, TikTok ou Instagram',
  'home.sellers.2': 'Moins de « ça va m’aller ? », plus de messages prêts à acheter',
  'home.sellers.3': 'Images et vidéos Statut avec le nom de ta boutique et ton prix',
  'home.sellers.4': 'Les vues, essayages et clics WhatsApp de chaque produit',
  'home.buyers.title': 'Pour les clients',
  'home.buyers.tag': 'Zéro hésitation',
  'home.buyers.1': 'Voir la tenue sur sa propre photo avant de commander',
  'home.buyers.2': 'Aucune application à installer : tout s’ouvre dans le navigateur',
  'home.buyers.3': 'Leur photo reste privée et est effacée sous 24 h',
  'home.buyers.4': 'Commander sur WhatsApp avec son look joint',
  'home.status.eyebrow': 'Aperçu Statut',
  'home.status.title': 'Prêt pour ton Statut WhatsApp',
  'home.status.subtitle': 'Exemples d’images 9:16 créées par TryOnMe à partir de l’essayage ci-dessus, en français et en anglais.',
  'home.status.alt': 'Exemple d’image Statut WhatsApp créée par TryOnMe',
  'home.facts.time': 'par essayage',
  'home.facts.apps': 'application à installer',
  'home.facts.privacy': 'de conservation max. des photos clients',
  'home.facts.languages': 'langues',
  'home.faq.eyebrow': 'Réponses',
  'home.faq.title': 'Questions fréquentes',
  'home.faq.q1': 'Est-ce gratuit pour commencer ?',
  'home.faq.a1': 'Oui. TryOnMe est gratuit pendant le pilote à Douala et Yaoundé : chaque boutique reçoit des crédits offerts (essayages clients, photos portées, vidéos Statut) et les recharges sont gratuites sur demande.',
  'home.faq.q2': 'Mes clients ont-ils besoin d’une application ?',
  'home.faq.a2': 'Non. Tes clients touchent ton lien sur WhatsApp, TikTok ou Instagram et tout s’ouvre dans leur navigateur.',
  'home.faq.q3': 'Que deviennent les photos des clients ?',
  'home.faq.a3': 'Elles restent privées : seul le client voit son résultat. Les selfies sont effacés automatiquement sous 24 h, et le client peut supprimer un essayage à tout moment. Le vendeur ne voit un look que si le client choisit de l’envoyer.',
  'home.faq.q4': 'Quels vêtements marchent le mieux ?',
  'home.faq.a4': 'Un seul vêtement par photo, de face, sur cintre, mannequin ou posé à plat sur un fond uni : robes, kaba, ensembles en wax, chemises, vestes et pantalons. Tu peux recadrer les autres articles dans l’application.',
  'home.cta.badge': 'Fait pour les vendeurs africains',
  'home.cta.title': 'Commence à vendre avec l’essayage dès aujourd’hui',
  'home.cta.body': 'On commence à Douala et Yaoundé, pour les vendeurs des réseaux sociaux de toute l’Afrique.',
  'home.footer.made': 'Fait avec fierté pour les vendeurs de mode africains',
  'home.footer.place': 'Douala et Yaoundé, Cameroun',
  'shop.edit': 'Modifier la boutique',
  'result.delete': 'Supprimer cet essayage',
  'result.deleteConfirm': 'Supprimer ta photo et ce résultat ? Si tu as envoyé ton look, le vendeur ne le verra plus.',
  'result.deleteYes': 'Supprimer',
  'result.deleted': 'Essayage supprimé : ta photo et le résultat sont effacés.',
  'result.deleteError': 'Impossible de supprimer pour le moment. Réessaie.',
  'selfie.retention': 'Elle est effacée automatiquement sous 24 h.',
  'share.active': 'Lien actif',
  'share.paused': 'Lien en pause',
  'share.pausedHint': 'Tes clients voient « plus disponible » jusqu’à ce que tu le réactives. Le lien reste le même.',
  'share.toggleError': 'Impossible de modifier le lien. Réessaie.',
  'still.history': 'Tes photos portées',
  'still.another': 'Nouvelle photo portée avec',
  'status.image.refresh': 'Mettre à jour l’image Statut',
  'status.image.refreshHint': 'Reprend le nom, la langue, le titre et le prix actuels. Gratuit.',
  'shop.update': 'Enregistrer',
  'shop.languageHint': 'Utilisée sur tes images Statut et tes messages WhatsApp.',
  'photo.lowResPerson': 'Photo petite ({width}×{height} px) : les photos portées sortent à cette taille, elles seront floues. Utilise la photo d’origine de l’appareil, pas une capture ni une copie WhatsApp.',
  'photo.lowResGarment': 'Photo très petite ({width}×{height} px) : les détails du tissu peuvent se perdre. Demande une version plus grande à ton fournisseur si possible.',
  'products.title': 'Tes produits',
  'products.empty': 'Pas encore de produit. Ajoute le premier !',
  'products.add': 'Ajouter un produit',
  'productForm.title': 'Nouveau produit',
  'productForm.photo': 'Photo du produit',
  'productForm.photoHint':
    'Un seul article, de face, fond uni (cintre, mannequin ou posé à plat). Pas de montage, pas de photo en plus, pas d’accessoires.',
  'productForm.name': 'Nom du produit',
  'productForm.category': 'Type',
  'productForm.price': 'Prix ({currency})',
  'productForm.description': 'Description (facultatif)',
  'productForm.publish': 'Publier',
  'productForm.publishing': 'Publication…',
  'productForm.cancel': 'Annuler',
  'category.full_body': 'Robe / tenue complète',
  'category.upper_body': 'Haut / chemise',
  'category.outer': 'Manteau / veste / gilet',
  'category.shoes': 'Chaussures / sandales',
  'category.auto': 'Autre (l’IA choisit)',
  'category.lower_body': "Pantalon / jupe (photo portée par quelqu'un)",
  'share.copy': 'Copier le lien',
  'share.copied': 'Lien copié !',
  'share.share': 'Partager',
  'share.whatsapp': 'Partager sur WhatsApp',
  'share.message': 'Essaie « {title} » sur toi avant de commander 👉 {url}',
  'share.unavailable': 'Lien de partage géré par TryOnMe',
  'models.title': 'Tes modèles',
  'models.empty': "Ajoute-toi (« Moi ») ou une personne d'accord, pour créer des photos portées.",
  'models.add': 'Ajouter un modèle',
  'models.kind': 'Qui est sur la photo ?',
  'models.kind.self': 'Moi',
  'models.kind.other': 'Une autre personne',
  'models.name': 'Nom',
  'models.photo': "Photo (debout, face à l'appareil, remplissant l'image de la tête aux hanches)",
  'models.consentTitle': 'Consentement',
  'models.consent.self':
    "J'accepte que TryOnMe crée des images de moi portant mes produits, pour ma boutique et ses publications.",
  'models.consent.other':
    "Cette personne a accepté que TryOnMe crée des images d'elle portant mes produits, pour ma boutique et ses publications.",
  'models.save': 'Enregistrer le modèle',
  'models.saving': 'Enregistrement…',
  'models.noMarketing': 'Le consentement ne couvre pas la publicité',
  'still.create': 'Créer la photo portée',
  'still.chooseModel': 'Modèle',
  'still.generate': 'Créer',
  'still.needModel': "Ajoute d'abord un modèle (dans « Tes modèles »).",
  'still.working': 'Création de la photo portée…',
  'still.done': 'Photo portée prête',
  'still.open': 'Ouvrir la photo',
  'still.alt': '{title} porté par {model}',
  'studioError.consent_required': "Confirme le consentement, s'il te plaît.",
  'studioError.marketing_consent_required': 'Le consentement de ce modèle ne couvre pas les photos publicitaires.',
  'studioError.daily_limit_reached': "Tu as atteint la limite de photos portées pour aujourd'hui. Réessaie demain.",
  'studioError.demo_limit_reached': 'La démo a atteint sa limite de générations pour le moment.',
  'studioError.generation_failed': 'Impossible de créer cette photo. Essaie une autre photo de modèle ou de produit.',
  'status.title': 'Pour ton Statut WhatsApp et TikTok',
  'status.image.create': "Créer l'image Statut",
  'status.video.create': 'Créer la vidéo Statut',
  'status.video.hint': 'Environ une minute.',
  'status.working': 'Création…',
  'status.share': 'Partager',
  'status.download': 'Télécharger',
  'status.copyCaption': 'Copier la légende',
  'status.captionCopied': 'Légende copiée !',
  'status.imageAlt': 'Image Statut de {title}',
  'studioError.social_consent_required': 'Le consentement de ce modèle ne couvre pas les publications sur les réseaux.',
  'studioError.composition_failed': 'Impossible de créer ce média. Réessaie.',
  'crop.frame': 'Cadre de recadrage. Flèches pour déplacer, Maj + flèches pour redimensionner.',
  'crop.reset': 'Réinitialiser',
  'crop.skip': 'Passer',
  'crop.apply': 'Utiliser ce cadrage',
  'crop.edit': 'Recadrer',
  'crop.productHint': 'Cadre un seul article. Laisse de côté montages, petites photos et accessoires.',
  'crop.modelHint': 'Aligne la silhouette : la tête en haut du cadre, les hanches en bas.',
  'photo.choose': 'Choisir une photo',
  'photo.change': 'Changer de photo',
  'photo.formats': 'JPG ou PNG, 10 Mo max',
  'home.badge': 'Essayage virtuel pour vendeurs sur les réseaux',
  'home.step1.title': 'Photographie ton article',
  'home.step1.body': 'Sur cintre ou à plat. Recadre le reste d’un geste.',
  'home.step2.title': 'Photo portée et Statut',
  'home.step2.body': 'Ton article porté par toi ou ton modèle, prêt pour ton Statut WhatsApp et TikTok.',
  'home.step3.title': 'Tes clients essaient et commandent',
  'home.step3.body': 'Ils se voient avec, puis t’écrivent sur WhatsApp avec leur look.',
  'home.tile1': 'Photo sur cintre',
  'home.tile2': 'Porté, prêt pour ton Statut',
  'studio.tabs.products': 'Produits',
  'studio.tabs.models': 'Modèles',
  'studio.tabs.looks': 'Looks',
  'studio.stats.views': 'Vues',
  'studio.stats.tryOns': 'Essayages',
  'studio.stats.whatsapp': 'WhatsApp',
  'studio.stats.looks': 'Looks',
  'studio.productsHint': 'Partage un lien : tes clients essaient avant de commander.',
  'studio.modelsHint': 'Qui porte tes articles sur tes photos portées et tes Statuts.',
  'studio.looksHint': 'Les looks que tes clients ont choisi de t’envoyer.',
  'product.trust.private': 'Ta photo reste privée',
  'product.trust.ai': 'Aperçu IA',
  'progress.title': 'Ton essayage arrive',
  'selfie.tip.facing': 'Face à l’appareil, debout',
  'selfie.tip.frame': 'Remplis l’image de la tête aux hanches',
  'selfie.tip.frameFeet': 'De la tête aux pieds : tes pieds doivent être visibles',
  'selfie.tip.light': 'Bonne lumière, fond uni',
  'selfie.tip.alone': 'Seul·e sur la photo',
  'studioError.invalid_shop_name': 'Indique un nom de boutique (80 caractères max).',
  'studioError.invalid_whatsapp': 'Ce numéro WhatsApp ne correspond pas au pays choisi.',
  'studioError.unsupported_country': "Ce pays n'est pas encore pris en charge.",
  'studioError.invalid_title': 'Indique un nom de produit (120 caractères max).',
  'studioError.invalid_price': 'Indique un prix valide, par exemple 15000.',
  'studioError.image_too_small': 'Cette photo est trop petite. Utilise une photo plus grande et nette.',
  'studioError.unsupported_image': 'Utilise une photo JPG ou PNG.',
  'studioError.image_too_large': 'Cette photo est trop lourde (10 Mo max).',
  'studioError.missing_photo': 'Ajoute une photo du produit.',
  'studioError.network': 'Pas de connexion. Vérifie ton internet et réessaie.',
  'studioError.generic': "Un souci s'est produit. Réessaie.",
  'notFound.title': 'Page introuvable',
  'notFound.back': "Retour à l'accueil",
}

export const MESSAGES: Record<Locale, Messages> = { en, fr }

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value)
}

/** Fills `{placeholders}`; unknown placeholders are left visible to catch bugs. */
export function translate(
  locale: Locale,
  key: MessageKey,
  values: Record<string, string | number> = {},
): string {
  return MESSAGES[locale][key].replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  )
}

/** Picks the first supported language from the browser's preference list. */
export function negotiateLocale(preferences: readonly string[]): Locale | undefined {
  for (const tag of preferences) {
    const primary = tag.toLowerCase().split('-')[0] ?? ''
    if (isLocale(primary)) return primary
  }
  return undefined
}
